<template>
  <div class="dashboard-container">
    <!-- Header Section -->
    <div class="dashboard-header mb-4">
      <el-row :gutter="20" align="middle">
        <el-col :xs="24" :sm="12" :md="8">
          <div class="header-info">
            <h1 class="dashboard-title">
              <i class="mdi mdi-view-dashboard-outline"></i>
              {{ license.client || 'Business' }} Dashboard
            </h1>
            <p class="dashboard-subtitle">Real-time business insights and analytics</p>
          </div>
        </el-col>
        <el-col :xs="24" :sm="12" :md="16">
          <div class="header-filters">
            <el-form :inline="true" size="small">
              <el-form-item label="Period">
                <el-date-picker
                  v-model="dateRange"
                  type="daterange"
                  range-separator="to"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  :shortcuts="dateShortcuts"
                  @change="fetchDashboardData"
                />
              </el-form-item>
              <el-form-item label="Store">
                <el-select v-model="selectedStore" placeholder="All Stores" @change="fetchDashboardData">
                  <el-option label="All Stores" value="all"></el-option>
                  <el-option v-for="store in stores" :key="store.id" :label="store.name" :value="store.id" />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" icon="el-icon-refresh" @click="refreshDashboard" :loading="loading">
                  Refresh
                </el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <el-card>
        <div class="loading-content">
          <i class="el-icon-loading"></i>
          <p>Loading dashboard data...</p>
        </div>
      </el-card>
    </div>

    <!-- Dashboard Content -->
    <div v-else>
      <!-- KPI Overview Cards -->
      <div class="kpi-section mb-4">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="6" v-for="kpi in kpiCards" :key="kpi.title" class="mb-3">
            <el-card :body-style="{ padding: '20px' }" class="kpi-card" :class="kpi.class">
              <div class="kpi-content">
                <div class="kpi-info">
                  <h4 class="kpi-title">{{ kpi.title }}</h4>
                  <h2 class="kpi-value">{{ kpi.value }}</h2>
                  <div class="kpi-comparison">
                    <span :class="['trend', kpi.trend > 0 ? 'positive' : 'negative']">
                      <i :class="kpi.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(kpi.trend) }}%
                    </span>
                    <span class="comparison-text">vs last period</span>
                  </div>
                </div>
                <div class="kpi-icon">
                  <i :class="`mdi ${kpi.icon}`"></i>
                </div>
              </div>
              <div class="kpi-sparkline">
                <line-chart :chart-data="kpi.sparklineData" :chart-options="sparklineOptions" :height="40"/>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <!-- Business Metrics Row -->
      <el-row :gutter="20" class="mb-4">
        <!-- Sales Performance -->
        <el-col :xs="24" :md="12" :lg="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-chart-line"></i> Sales Performance</h3>
              <el-dropdown @command="changeSalesView">
                <el-button size="mini" type="text">
                  {{ salesViewType }} <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="hourly">Hourly</el-dropdown-item>
                  <el-dropdown-item command="daily">Daily</el-dropdown-item>
                  <el-dropdown-item command="weekly">Weekly</el-dropdown-item>
                  <el-dropdown-item command="monthly">Monthly</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
            <div class="chart-container">
              <line-chart :chart-data="salesPerformanceData" :chart-options="salesChartOptions" :height="250"/>
            </div>
            <div class="chart-stats">
              <div class="stat-item">
                <span class="stat-label">Peak Hour</span>
                <span class="stat-value">{{ salesPerformance.peak_hour || '2:00 PM' }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Avg. Transaction</span>
                <span class="stat-value">{{ formatCurrency(salesPerformance.average_transaction || 0) }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Conversion Rate</span>
                <span class="stat-value">{{ salesPerformance.conversion_rate || 68.4 }}%</span>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- Top Products -->
        <el-col :xs="24" :md="12" :lg="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-package-variant"></i> Top Products</h3>
              <el-tag size="mini" type="info">{{ getPeriodText() }}</el-tag>
            </div>
            <div class="product-list">
              <div v-for="(product, index) in topProducts" :key="index" class="product-item">
                <div class="product-rank">{{ index + 1 }}</div>
                <div class="product-info">
                  <div class="product-name">{{ product.name }}</div>
                  <div class="product-stats">
                    <span class="units">{{ product.units }} units</span>
                    <span class="revenue">{{ formatCurrency(product.revenue) }}</span>
                  </div>
                  <el-progress
                    :percentage="product.percentage"
                    :stroke-width="4"
                    :show-text="false"
                    :color="getProgressColor(product.percentage)"
                  />
                </div>
                <div class="product-trend" :class="product.trend > 0 ? 'up' : 'down'">
                  <i :class="product.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                  {{ Math.abs(product.trend) }}%
                </div>
              </div>
            </div>
            <div class="view-all-link">
              <router-link to="/items">View all products <i class="el-icon-arrow-right"></i></router-link>
            </div>
          </el-card>
        </el-col>

        <!-- Category Distribution -->
        <el-col :xs="24" :md="12" :lg="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-shape"></i> Sales by Category</h3>
              <el-switch v-model="showCategoryValue" active-text="Value" inactive-text="Units"/>
            </div>
            <div class="chart-container">
              <doughnut-chart :chart-data="categoryDistributionData" :chart-options="doughnutOptions" :height="220"/>
            </div>
            <div class="category-legend">
              <div v-for="(cat, index) in categoryData" :key="index" class="legend-item">
                <span class="legend-color" :style="{backgroundColor: cat.color}"></span>
                <span class="legend-name">{{ cat.name }}</span>
                <span class="legend-value">{{ formatCurrency(cat.value) }}</span>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- Analytics Row -->
      <el-row :gutter="20" class="mb-4">
        <!-- Payment Methods Analysis -->
        <el-col :xs="24" :md="12" :lg="6" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-credit-card-outline"></i> Payment Methods</h3>
            </div>
            <div class="payment-methods">
              <div v-for="method in paymentMethods" :key="method.name" class="payment-method">
                <div class="method-header">
                  <i :class="method.icon"></i>
                  <span class="method-name">{{ method.name }}</span>
                  <span class="method-percentage">{{ method.percentage }}%</span>
                </div>
                <el-progress
                  :percentage="method.percentage"
                  :stroke-width="6"
                  :show-text="false"
                  :color="method.color"
                />
                <div class="method-stats">
                  <span class="transactions">{{ method.transactions }} transactions</span>
                  <span class="amount">{{ formatCurrency(method.amount) }}</span>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- Employee Performance -->
        <el-col :xs="24" :md="12" :lg="6" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-account-group"></i> Top Performers</h3>
            </div>
            <div class="employee-list">
              <div v-for="(employee, index) in topPerformers" :key="index" class="employee-item">
                <div class="employee-rank">{{ index + 1 }}</div>
                <div class="employee-info">
                  <div class="employee-name">{{ employee.name }}</div>
                  <div class="employee-stats">
                    <span class="transactions">{{ employee.transactions }} sales</span>
                    <span class="revenue">{{ formatCurrency(employee.total_sales) }}</span>
                  </div>
                  <div class="employee-rating">
                    <el-rate v-model="employee.rating" disabled show-score text-color="#ff9900" score-template="{value}"></el-rate>
                  </div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- Inventory Alerts -->
        <el-col :xs="24" :md="12" :lg="6" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-alert-outline"></i> Inventory Alerts</h3>
              <el-badge :value="inventoryAlerts.length" class="item"/>
            </div>
            <div class="inventory-alerts">
              <div v-for="(alert, idx) in inventoryAlerts" :key="alert.id + '-' + idx" class="alert-item" :class="alert.type">
                <div class="alert-icon">
                  <i :class="getAlertIcon(alert.type)"></i>
                </div>
                <div class="alert-content">
                  <div class="alert-title">{{ alert.item }}</div>
                  <div class="alert-message">{{ alert.message }}</div>
                  <div class="alert-departmen alert-message">
                    <span class="department-labe">{{ alert.department }}</span>
                  </div>
                </div>
                <div class="alert-action">
                  <el-button size="mini" type="text" @click="handleAlertAction(alert)">Action</el-button>
                </div>
              </div>
            </div>
            <div class="view-all-link">
              <router-link to="/stock">Manage inventory <i class="el-icon-arrow-right"></i></router-link>
            </div>
          </el-card>
        </el-col>

        <!-- Recent Activity -->
        <el-col :xs="24" :md="12" :lg="6" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-history"></i> Recent Activity</h3>
            </div>
            <el-timeline>
              <el-timeline-item
                v-for="(activity, index) in recentActivity"
                :key="index"
                :icon="activity.icon"
                :type="activity.type"
                :timestamp="activity.time"
                placement="top">
                {{ activity.content }}
              </el-timeline-item>
            </el-timeline>
          </el-card>
        </el-col>
      </el-row>

      <!-- Detailed Analytics Row -->
      <el-row :gutter="20" class="mb-4">
        <!-- Sales Comparison -->
        <el-col :xs="24" :lg="16" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-chart-bar"></i> Sales Comparison</h3>
              <el-button-group size="mini">
                <el-button :type="comparisonView === 'daily' ? 'primary' : ''" @click="comparisonView = 'daily'">Daily</el-button>
                <el-button :type="comparisonView === 'weekly' ? 'primary' : ''" @click="comparisonView = 'weekly'">Weekly</el-button>
                <el-button :type="comparisonView === 'monthly' ? 'primary' : ''" @click="comparisonView = 'monthly'">Monthly</el-button>
              </el-button-group>
            </div>
            <div class="chart-container">
              <bar-chart :chart-data="salesComparisonData" :chart-options="comparisonChartOptions" :height="300"/>
            </div>
          </el-card>
        </el-col>

        <!-- Quick Stats -->
        <el-col :xs="24" :lg="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-speedometer"></i> Quick Stats</h3>
            </div>
            <div class="quick-stats">
              <div v-for="(stat, index) in quickStats" :key="index" class="stat-card">
                <div class="stat-icon" :class="stat.color">
                  <i :class="stat.icon"></i>
                </div>
                <div class="stat-details">
                  <div class="stat-value">{{ stat.value }}</div>
                  <div class="stat-label">{{ stat.title }}</div>
                  <div class="stat-change" :class="stat.change > 0 ? 'positive' : 'negative'">
                    {{ stat.change > 0 ? '+' : '' }}{{ stat.change }}% this month
                  </div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- Last update time -->
    <div class="dashboard-footer">
      <span class="last-update">
        <i class="el-icon-time"></i> Last updated: {{ lastUpdateTime }}
      </span>
      <span class="next-update" v-if="nextUpdateIn > 0">
        Next update in: {{ nextUpdateIn }}s
      </span>
    </div>
  </div>
</template>

<script>
import BarChart from '../_Partials/Charts/BarChart'
import PieChart from '../_Partials/Charts/PieChart'
import DoughnutChart from '../_Partials/Charts/DoughnutChart'
import LineChart from '../_Partials/Charts/LineChart'
import DashboardRobustService from '@/services/dashboard.robust.service'
import CurrencyMixin from '@/mixins/CurrencyMixin'
import { mapState } from 'vuex'

export default {
  name: 'HomeNew',
  components: {
    BarChart, PieChart, DoughnutChart, LineChart
  },
  mixins: [CurrencyMixin],
  data () {
    return {
      loading: false,
      dateRange: [],
      selectedStore: 'all',
      salesViewType: 'Daily',
      showCategoryValue: true,
      comparisonView: 'daily',
      lastUpdateTime: new Date().toLocaleString(),
      nextUpdateIn: 300, // 5 minutes
      refreshInterval: null,
      countdownInterval: null,

      // Dashboard data
      dashboardData: null,
      kpiCards: [],
      topProducts: [],
      categoryData: [],
      paymentMethods: [],
      topPerformers: [],
      inventoryAlerts: [],
      recentActivity: [],
      salesPerformance: {},
      quickStats: [],
      stores: [],

      // Date shortcuts
      dateShortcuts: [{
        text: 'Today',
        value: () => {
          const end = new Date()
          const start = new Date()
          return [start, end]
        }
      }, {
        text: 'Yesterday',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 1)
          end.setDate(end.getDate() - 1)
          return [start, end]
        }
      }, {
        text: 'Last 7 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 6)
          return [start, end]
        }
      }, {
        text: 'Last 30 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 29)
          return [start, end]
        }
      }, {
        text: 'This month',
        value: () => {
          const end = new Date()
          const start = new Date(end.getFullYear(), end.getMonth(), 1)
          return [start, end]
        }
      }, {
        text: 'Last month',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setMonth(start.getMonth() - 1)
          start.setDate(1)
          end.setDate(0)
          return [start, end]
        }
      }],

      // Sparkline options
      sparklineOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        elements: {
          line: { borderWidth: 2 },
          point: { radius: 0 }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        },
        plugins: {
          tooltip: { enabled: false }
        }
      },

      // Sales Performance Data
      salesPerformanceData: {
        labels: ['6AM', '8AM', '10AM', '12PM', '2PM', '4PM', '6PM', '8PM', '10PM'],
        datasets: [{
          label: 'Today',
          data: [4500, 6200, 8900, 12400, 15800, 14200, 11800, 8900, 5200],
          borderColor: '#409EFF',
          backgroundColor: 'rgba(64, 158, 255, 0.1)',
          tension: 0.4
        }, {
          label: 'Yesterday',
          data: [3800, 5400, 7200, 10800, 13200, 12800, 10200, 7600, 4800],
          borderColor: '#E6A23C',
          backgroundColor: 'rgba(230, 162, 60, 0.1)',
          tension: 0.4
        }]
      },

      salesChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      },

      // Category distribution data
      categoryDistributionData: {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },

      doughnutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        }
      },

      // Sales comparison data
      salesComparisonData: {
        labels: [],
        datasets: []
      },

      comparisonChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.Auth.user,
      license: state => state.Auth.license
    })
  },
  async mounted () {
    // Set default date range to last 30 days
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    this.dateRange = [start, end]

    await this.fetchStores()
    await this.fetchDashboardData()
    this.startAutoRefresh()
  },
  beforeDestroy () {
    this.stopAutoRefresh()
  },
  methods: {
    async fetchStores () {
      try {
        this.stores = await this.$departmentService.getDepartments()
      } catch (error) {
        this.$message.error('Failed to load stores')
      }
    },

    async fetchDashboardData () {
      try {
        this.loading = true

        const filters = {
          start_date: this.dateRange[0],
          end_date: this.dateRange[1],
          department_id: this.selectedStore !== 'all' ? this.selectedStore : undefined
        }

        const dashboardService = new DashboardRobustService(this.$http)
        const response = await dashboardService.getDashboardData(filters)

        if (response.status === 'success') {
          this.dashboardData = response.data
          this.updateDashboardComponents()
        } else {
          this.$message.error('Failed to fetch dashboard data')
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        this.$message.error('Error loading dashboard data')
      } finally {
        this.loading = false
        this.lastUpdateTime = new Date().toLocaleString()
      }
    },

    updateDashboardComponents () {
      if (!this.dashboardData) return

      // Update KPI cards
      this.kpiCards = this.dashboardData.kpi_metrics || []

      // Update sales performance
      this.salesPerformance = this.dashboardData.sales_performance || {}

      // Update top products
      this.topProducts = this.dashboardData.top_products || []

      // Update category data
      this.categoryData = this.dashboardData.category_distribution || []
      this.updateCategoryChart()

      // Update payment methods
      this.paymentMethods = this.dashboardData.payment_methods || []

      // Update employee performance
      this.topPerformers = this.dashboardData.top_performers || []

      // Update inventory alerts
      this.inventoryAlerts = this.dashboardData.inventory_alerts || []

      // Update recent activities
      this.recentActivity = this.dashboardData.recent_activity || []

      // Update quick stats
      this.quickStats = this.dashboardData.quick_stats || []

      // Update sales comparison
      this.updateSalesComparisonChart()
    },

    updateCategoryChart () {
      this.categoryDistributionData.labels = this.categoryData.map(cat => cat.name)
      this.categoryDistributionData.datasets[0].data = this.categoryData.map(cat =>
        this.showCategoryValue ? cat.value : cat.units
      )
      this.categoryDistributionData.datasets[0].backgroundColor = this.categoryData.map(cat => cat.color)
    },

    updateSalesComparisonChart () {
      if (this.dashboardData.sales_comparison) {
        // This would be implemented based on the actual data structure
        this.salesComparisonData = {
          labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
          datasets: [{
            label: 'This Week',
            data: [18500, 22300, 19800, 24500, 28900, 35200, 31800],
            backgroundColor: '#409EFF'
          }, {
            label: 'Last Week',
            data: [16200, 19800, 18500, 21300, 25600, 32100, 29400],
            backgroundColor: '#E6A23C'
          }]
        }
      }
    },

    getProgressColor (percentage) {
      if (percentage >= 80) return '#67C23A'
      if (percentage >= 60) return '#409EFF'
      if (percentage >= 40) return '#E6A23C'
      return '#F56C6C'
    },

    getAlertIcon (type) {
      const icons = {
        critical: 'mdi mdi-alert-circle',
        warning: 'mdi mdi-alert',
        info: 'mdi mdi-information'
      }
      return icons[type] || 'mdi mdi-alert'
    },

    changeSalesView (view) {
      this.salesViewType = view.charAt(0).toUpperCase() + view.slice(1)
      // Here you would fetch data for the new view type
      this.updateSalesPerformanceData(view)
    },

    updateSalesPerformanceData (view) {
      // Update chart data based on view type
      switch (view) {
        case 'hourly':
          this.salesPerformanceData = this.salesPerformance.hourly || this.salesPerformanceData
          break
        case 'daily':
          this.salesPerformanceData = this.salesPerformance.daily || this.salesPerformanceData
          break
        case 'weekly':
          this.salesPerformanceData = this.salesPerformance.weekly || this.salesPerformanceData
          break
        case 'monthly':
          this.salesPerformanceData = this.salesPerformance.monthly || this.salesPerformanceData
          break
      }
    },

    handleAlertAction (alert) {
      this.$message.info(`Handling alert for ${alert.item}`)
      // Implement alert action logic
    },

    refreshDashboard () {
      this.fetchDashboardData()
    },

    getPeriodText () {
      if (!this.dateRange || this.dateRange.length !== 2) return 'Last 30 days'

      const start = new Date(this.dateRange[0])
      const end = new Date(this.dateRange[1])
      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24))

      if (daysDiff === 1) return 'Today'
      if (daysDiff === 2) return 'Yesterday'
      if (daysDiff <= 7) return 'Last 7 days'
      if (daysDiff <= 30) return 'Last 30 days'
      if (daysDiff <= 90) return 'Last 3 months'
      return 'Custom period'
    },

    startAutoRefresh () {
      // Refresh every 5 minutes
      this.refreshInterval = setInterval(() => {
        this.fetchDashboardData()
      }, 300000)

      // Countdown timer
      this.countdownInterval = setInterval(() => {
        if (this.nextUpdateIn > 0) {
          this.nextUpdateIn--
        }
      }, 1000)
    },

    stopAutoRefresh () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.dashboard-header {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.dashboard-title {
  margin: 0;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
}

.dashboard-subtitle {
  margin: 5px 0 0 0;
  color: #909399;
  font-size: 14px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-content {
  text-align: center;
  color: #909399;
}

.loading-content i {
  font-size: 32px;
  margin-bottom: 10px;
}

.kpi-card {
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.kpi-card.revenue {
  border-left: 4px solid #67C23A;
}

.kpi-card.orders {
  border-left: 4px solid #409EFF;
}

.kpi-card.aov {
  border-left: 4px solid #E6A23C;
}

.kpi-card.customers {
  border-left: 4px solid #F56C6C;
}

.kpi-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.kpi-info {
  flex: 1;
}

.kpi-title {
  margin: 0 0 8px 0;
  color: #606266;
  font-size: 14px;
  font-weight: 500;
}

.kpi-value {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.kpi-comparison {
  display: flex;
  align-items: center;
  gap: 5px;
}

.trend {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
}

.trend.positive {
  color: #67C23A;
}

.trend.negative {
  color: #F56C6C;
}

.comparison-text {
  color: #909399;
  font-size: 12px;
}

.kpi-icon {
  font-size: 32px;
  color: #DCDFE6;
  margin-left: 15px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.chart-container {
  margin-bottom: 15px;
}

.chart-stats {
  display: flex;
  justify-content: space-between;
  padding-top: 15px;
  border-top: 1px solid #EBEEF5;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  color: #909399;
  font-size: 12px;
  margin-bottom: 4px;
}

.stat-value {
  color: #303133;
  font-weight: 600;
  font-size: 14px;
}

.product-list, .employee-list {
  max-height: 300px;
  overflow-y: auto;
}

.product-item, .employee-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #EBEEF5;
}

.product-item:last-child, .employee-item:last-child {
  border-bottom: none;
}

.product-rank, .employee-rank {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #409EFF;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-right: 12px;
}

.product-info, .employee-info {
  flex: 1;
}

.product-name, .employee-name {
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.product-stats, .employee-stats {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

.product-trend {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 10px;
}

.product-trend.up {
  color: #67C23A;
}

.product-trend.down {
  color: #F56C6C;
}

.employee-rating {
  margin-top: 4px;
}

.category-legend {
  max-height: 200px;
  overflow-y: auto;
}

.legend-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #EBEEF5;
}

.legend-item:last-child {
  border-bottom: none;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 8px;
}

.legend-name {
  flex: 1;
  font-size: 14px;
  color: #303133;
}

.legend-value {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}

.payment-methods {
  max-height: 300px;
  overflow-y: auto;
}

.payment-method {
  margin-bottom: 20px;
}

.payment-method:last-child {
  margin-bottom: 0;
}

.method-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.method-header i {
  margin-right: 8px;
  font-size: 16px;
}

.method-name {
  flex: 1;
  font-weight: 600;
  color: #303133;
}

.method-percentage {
  font-weight: 600;
  color: #409EFF;
}

.method-stats {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.inventory-alerts {
  max-height: 300px;
  overflow-y: auto;
}

.alert-item {
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  border-left: 4px solid;
}

.alert-item.critical {
  background: #FEF0F0;
  border-left-color: #F56C6C;
}

.alert-item.warning {
  background: #FDF6EC;
  border-left-color: #E6A23C;
}

.alert-item.info {
  background: #F0F9FF;
  border-left-color: #409EFF;
}

.alert-icon {
  margin-right: 12px;
  font-size: 18px;
}

.alert-item.critical .alert-icon {
  color: #F56C6C;
}

.alert-item.warning .alert-icon {
  color: #E6A23C;
}

.alert-item.info .alert-icon {
  color: #409EFF;
}

.alert-content {
  flex: 1;
}

.alert-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 2px;
}

.alert-message {
  font-size: 12px;
  color: #606266;
}

.alert-department {
  margin-top: 6px;
}

.quick-stats {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  background: #e9ecef;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 20px;
  color: white;
}

.stat-icon.success {
  background: #67C23A;
}

.stat-icon.primary {
  background: #409EFF;
}

.stat-icon.warning {
  background: #E6A23C;
}

.stat-icon.danger {
  background: #F56C6C;
}

.stat-icon.info {
  background: #909399;
}

.stat-details {
  flex: 1;
}

.stat-details .stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 4px;
}

.stat-details .stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.stat-change {
  font-size: 11px;
  font-weight: 600;
}

.stat-change.positive {
  color: #67C23A;
}

.stat-change.negative {
  color: #F56C6C;
}

.view-all-link {
  text-align: center;
  padding-top: 15px;
  border-top: 1px solid #EBEEF5;
}

.view-all-link a {
  color: #409EFF;
  text-decoration: none;
  font-size: 14px;
}

.view-all-link a:hover {
  text-decoration: underline;
}

.dashboard-footer {
  text-align: center;
  padding: 20px;
  color: #909399;
  font-size: 12px;
}

.last-update {
  margin-right: 20px;
}

.next-update {
  color: #409EFF;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 10px;
  }

  .dashboard-header {
    padding: 15px;
  }

  .dashboard-title {
    font-size: 20px;
  }

  .kpi-value {
    font-size: 24px;
  }

  .chart-stats {
    flex-direction: column;
    gap: 10px;
  }

  .stat-item {
    text-align: left;
  }
}
</style>
